import './DateFilter.scss';
import { ButtonGroup, Button, ToolbarSeparator } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useCallback, useState } from 'react';
import { DateFilterTab, VisualData } from '../../models/date-filter.model';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  selectTempPlanningVisualType,
  selectTempPlanningStartDate,
  selectTempPlanningVisualData,
  selectTempPlanningEndDate,
  setPlanningVisualType,
  setPlanningEndDate,
  setPlanningVisualData,
  setPlanningStartDate,
} from '../../store/slices/date-filter.slice';
import { RootState } from '../../../common/store';
import { DatasetID } from '../../../common/models/filter.model';
import FlyoutDropDownItem from '../flyouts/FlyoutDropDownItem';
import { calculateDatesFromNextPeriod } from 'common/hooks/utils';
import KwelloDatePicker from './KwelloDatePicker';
import { DROPDOWN_UNITS, DROPDOWN_VALUES } from './DateFilter';

enum DropdownOpen {
  NONE = -1,
  COUNT = 0,
  UNIT = 1,
}

export function PlanningDateFilter({ datasetId }: Readonly<DatasetID>) {
  const dispatch = useAppDispatch();
  const [dropdownIdOpen, setDropdownIdOpen] = useState<DropdownOpen>(DropdownOpen.NONE);

  const activeTab = useAppSelector((state: RootState) =>
    selectTempPlanningVisualType(state, datasetId)
  );
  const visualRepresentation = useAppSelector((state: RootState) =>
    selectTempPlanningVisualData(state, datasetId)
  );
  const startDate = useAppSelector((state: RootState) =>
    selectTempPlanningStartDate(state, datasetId)
  );
  const endDate = useAppSelector((state: RootState) => selectTempPlanningEndDate(state, datasetId));

  const setActiveTab = useCallback(
    (nextValue: DateFilterTab) => {
      dispatch(setPlanningVisualType({ datasetId, visualType: nextValue }));
    },
    [datasetId, dispatch]
  );

  const isTabActive = useCallback(
    (value: DateFilterTab) => {
      return value === activeTab;
    },
    [activeTab]
  );

  const setVisualRepresentationData = useCallback(
    (index: number, value: string | number) => {
      const next: VisualData = [...visualRepresentation];

      next[index] = value;

      if (index === 1) {
        const exists = DROPDOWN_VALUES[value].filter((v) => v == next[0]);
        next[0] = exists.length > 0 ? next[0] : DROPDOWN_VALUES[value].at(0) ?? 1;
      }

      const calculatedEndDate = calculateDatesFromNextPeriod(next);

      dispatch(setPlanningVisualData({ datasetId, visualData: next }));
      dispatch(
        setPlanningEndDate({ datasetId, date: new Date(calculatedEndDate?.start as string) })
      );
    },
    [visualRepresentation, datasetId, dispatch]
  );

  const openDateFilterDropdown = (id: DropdownOpen) => {
    setDropdownIdOpen((prev) => (prev === id ? -1 : id));
  };

  const isDropdownOpen = (id: DropdownOpen) => {
    return dropdownIdOpen === id;
  };

  return (
    <div className="date-filter-container">
      <div className="date-filter-title-container">
        <label className="k-label k-label-lg form-label-row">Date Range</label>
      </div>
      <div className="date-filter-tabs">
        <ButtonGroup className="button-group-tabs">
          <Button
            onClick={() => setActiveTab(DateFilterTab.NEXT)}
            selected={isTabActive(DateFilterTab.NEXT)}
            togglable={true}
            fillMode="flat"
          >
            Next
          </Button>
          <Button
            onClick={() => setActiveTab(DateFilterTab.BETWEEN)}
            selected={isTabActive(DateFilterTab.BETWEEN)}
            togglable={true}
            fillMode="flat"
          >
            Between
          </Button>
        </ButtonGroup>
      </div>
      <div className="date-filter-content">
        {isTabActive(DateFilterTab.NEXT) && (
          <div className="date-filter-content-tab-last">
            <div className="cell">
              <label className="k-label k-label-lg">Next</label>
            </div>
            <div className="cell-dropdown-values">
              <DropDownList
                value={visualRepresentation[0]}
                data={DROPDOWN_VALUES[visualRepresentation[1]]}
                onChange={(e) => setVisualRepresentationData(0, e.value)}
                size={'small'}
                itemRender={FlyoutDropDownItem}
                fillMode={'outline'}
                opened={isDropdownOpen(DropdownOpen.COUNT)}
                onOpen={() => openDateFilterDropdown(DropdownOpen.COUNT)}
                onClose={() => openDateFilterDropdown(DropdownOpen.NONE)}
              ></DropDownList>
            </div>
            <div className="cell-dropdown-units">
              <DropDownList
                value={visualRepresentation[1]}
                data={DROPDOWN_UNITS}
                onChange={(e) => setVisualRepresentationData(1, e.value)}
                size={'small'}
                itemRender={FlyoutDropDownItem}
                fillMode={'outline'}
                opened={isDropdownOpen(DropdownOpen.UNIT)}
                onOpen={() => openDateFilterDropdown(DropdownOpen.UNIT)}
                onClose={() => openDateFilterDropdown(DropdownOpen.NONE)}
              ></DropDownList>
            </div>
          </div>
        )}
        {isTabActive(DateFilterTab.BETWEEN) && (
          <div className="date-filter-content-tab-between">
            <div className="calendar-from-to">
              <div className="form-row">
                <div className="cell-label">
                  <label className="k-label label-up">From</label>
                </div>
                <div className="cell cell-datepicker">
                  <KwelloDatePicker
                    defaultValue={new Date(startDate)}
                    max={new Date(endDate)}
                    onChange={(e) =>
                      dispatch(setPlanningStartDate({ datasetId, date: e.value as Date }))
                    }
                    size={'small'}
                    width={150}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="cell-label">
                  <label className="k-label label-up">To</label>
                </div>{' '}
                <div className="cell-datepicker">
                  <KwelloDatePicker
                    defaultValue={new Date(endDate)}
                    min={new Date(startDate)}
                    onChange={(e) =>
                      dispatch(setPlanningEndDate({ datasetId, date: e.value as Date }))
                    }
                    size={'small'}
                    width={150}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToolbarSeparator />
    </div>
  );
}
